import React, { useState, useRef } from 'react';
import { Loader2, ArrowDown } from 'lucide-react';

interface PullToRefreshProps {
  onRefresh: () => Promise<void> | void;
  children: React.ReactNode;
}

export function PullToRefresh({ onRefresh, children }: PullToRefreshProps) {
  const [pullY, setPullY] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startY = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const THRESHOLD = 65;

  const getScrollTop = () => {
    if (typeof window === 'undefined') return 0;
    const mainEl = containerRef.current?.closest('main');
    if (mainEl) return mainEl.scrollTop;
    return window.scrollY || document.documentElement.scrollTop || 0;
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    // Only trigger if at the top of the scroll container
    const scrollTop = getScrollTop();
    if (scrollTop <= 2) {
      startY.current = e.touches[0].clientY;
    } else {
      startY.current = 0;
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (startY.current === 0 || isRefreshing) return;
    const scrollTop = getScrollTop();
    if (scrollTop > 2) {
      startY.current = 0;
      setPullY(0);
      return;
    }
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;
    if (diff > 0) {
      // Damping formula
      const damping = Math.min(diff * 0.45, 90);
      setPullY(damping);
    }
  };

  const handleTouchEnd = async () => {
    if (startY.current === 0 || isRefreshing) return;
    if (pullY >= THRESHOLD) {
      setIsRefreshing(true);
      setPullY(50);
      try {
        await onRefresh();
      } catch (err) {
        console.error('Pull-to-refresh error:', err);
      } finally {
        setTimeout(() => {
          setIsRefreshing(false);
          setPullY(0);
        }, 300);
      }
    } else {
      setPullY(0);
    }
    startY.current = 0;
  };

  return (
    <div
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className="relative min-h-full"
    >
      {/* Pull Indicator */}
      <div
        className="flex items-center justify-center transition-all overflow-hidden w-full text-brand-muted text-xs font-medium"
        style={{
          height: `${pullY}px`,
          opacity: pullY > 15 ? 1 : 0,
        }}
      >
        <div className="flex items-center space-x-2 py-2">
          {isRefreshing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin text-brand" />
              <span className="text-brand font-semibold">Updating workspace...</span>
            </>
          ) : (
            <>
              <ArrowDown
                className={`h-4 w-4 transition-transform ${
                  pullY >= THRESHOLD ? 'rotate-180 text-brand' : 'text-brand-muted'
                }`}
              />
              <span>{pullY >= THRESHOLD ? 'Release to refresh' : 'Pull down to refresh'}</span>
            </>
          )}
        </div>
      </div>

      {children}
    </div>
  );
}

export default PullToRefresh;
